/**
 * TEMP-H-011: Server-based Billing Hook
 * 
 * Migrated version of useBilling that uses unified API base configuration
 * and server-side auth instead of direct Supabase auth.
 */

import { useState, useEffect, useRef } from 'react';
import { API_ENDPOINTS, buildApiUrl, getApiHeaders, API_CONFIG } from '@/lib/api-base';
import { useDashboardServerStore } from '@/stores/dashboard-server';

export interface BillingStatus {
  hasActiveSubscription: boolean;
  balance: number;
  brand: string;
  isAdmin: boolean;
  billingBypass?: boolean;
}

export interface CreditBalance {
  balance: number;
  currency: string;
  brand: string;
  isAdmin: boolean;
  billingBypass?: boolean;
}

export interface AutoRefillSettings {
  enabled: boolean;
  minThreshold?: number;
  amount?: number;
}

export interface LedgerEntry {
  id: string;
  amount: number;
  type: 'credit' | 'debit';
  description: string;
  created_at: string;
}

export interface CreditLedger {
  ledger: LedgerEntry[];
  limit: number;
  offset: number;
  isAdmin: boolean;
  billingBypass?: boolean;
  message?: string;
}

/**
 * Enhanced fetch wrapper with retry logic and timeout
 */
async function billingApiRequest<T>(
  endpoint: string, 
  options: RequestInit = {}
): Promise<T> {
  const url = buildApiUrl(endpoint);
  const config: RequestInit = {
    headers: getApiHeaders(true), // Include auth
    credentials: 'include', // Include cookies for auth
    ...options,
  };

  let lastError: Error | null = null;
  
  for (let attempt = 1; attempt <= API_CONFIG.retryAttempts; attempt++) {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), API_CONFIG.timeout);
      
      const response = await fetch(url, {
        ...config,
        signal: controller.signal,
      });
      
      clearTimeout(timeoutId);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      return await response.json();
    } catch (error) {
      lastError = error instanceof Error ? error : new Error('Unknown error');
      
      if (attempt < API_CONFIG.retryAttempts) {
        await new Promise(resolve => 
          setTimeout(resolve, API_CONFIG.retryDelay * attempt)
        );
      }
    }
  }
  
  throw lastError || new Error('Request failed after retries');
}

export const useServerBilling = () => {
  const [status, setStatus] = useState<BillingStatus | null>(null);
  const [balance, setBalance] = useState<CreditBalance | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Fetch billing status via server API
  const fetchStatus = async () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    try {
      setLoading(true);
      setError(null);

      const result = await billingApiRequest<BillingStatus>(
        API_ENDPOINTS.billing.usage,
        {
          signal: abortControllerRef.current.signal,
        }
      );

      setStatus(result);
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') return;
      
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      setError(errorMessage);
      console.error('Error fetching billing status:', err);
      
      const { addNotification } = useDashboardServerStore.getState();
      addNotification({ 
        type: "error", 
        title: `Failed to load billing status: ${errorMessage}`, 
        duration: 4000 
      });
    } finally {
      setLoading(false);
    }
  };

  // Fetch balance via server API
  const fetchBalance = async () => {
    try {
      const result = await billingApiRequest<CreditBalance>(
        API_ENDPOINTS.billing.limits
      );

      setBalance(result);
    } catch (err) {
      console.error('Error fetching balance:', err);
      
      const { addNotification } = useDashboardServerStore.getState();
      addNotification({ 
        type: "error", 
        title: 'Failed to load balance', 
        duration: 4000 
      });
    }
  };

  // Update auto-refill settings via server API
  const updateAutoRefill = async (settings: AutoRefillSettings): Promise<boolean> => {
    try {
      const result = await billingApiRequest<{ success: boolean; error?: string }>(
        '/api/billing/auto-refill',
        {
          method: 'POST',
          body: JSON.stringify(settings),
        }
      );
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to update auto-refill settings');
      }

      const { addNotification } = useDashboardServerStore.getState();
      addNotification({ 
        type: "success", 
        title: 'Auto-refill settings updated successfully', 
        duration: 3000 
      });
      
      return true;
    } catch (err) {
      console.error('Error updating auto-refill:', err);
      
      const { addNotification } = useDashboardServerStore.getState();
      addNotification({ 
        type: "error", 
        title: 'Failed to update auto-refill', 
        duration: 4000 
      });
      
      return false;
    }
  };

  // Get credit ledger via server API
  const getCreditLedger = async (limit = 50, offset = 0): Promise<CreditLedger | null> => {
    try {
      const queryParams = new URLSearchParams({
        limit: limit.toString(),
        offset: offset.toString(),
      });

      const result = await billingApiRequest<CreditLedger>(
        `${API_ENDPOINTS.billing.history}?${queryParams.toString()}`
      );

      return result;
    } catch (err) {
      console.error('Error fetching credit ledger:', err);
      
      const { addNotification } = useDashboardServerStore.getState();
      addNotification({ 
        type: "error", 
        title: 'Failed to load credit ledger', 
        duration: 4000 
      });
      
      return null;
    }
  };

  // Create checkout session via server API
  const createCheckoutSession = async (
    type: 'subscription' | 'topup',
    amount?: 10 | 25 | 100,
    successUrl?: string,
    cancelUrl?: string
  ) => {
    try {
      const result = await billingApiRequest<{ url?: string; error?: string }>(
        '/api/billing/create-checkout-session',
        {
          method: 'POST',
          body: JSON.stringify({
            type,
            amount,
            successUrl: successUrl || window.location.href,
            cancelUrl: cancelUrl || window.location.href,
          }),
        }
      );
      
      if (result.url) {
        window.location.href = result.url;
      }
      
      return result;
    } catch (err) {
      console.error('Error creating checkout session:', err);
      
      const { addNotification } = useDashboardServerStore.getState();
      addNotification({ 
        type: "error", 
        title: 'Failed to create checkout session', 
        duration: 4000 
      });
      
      return null;
    }
  };

  // Refresh all billing data
  const refetch = async () => {
    await Promise.all([fetchStatus(), fetchBalance()]);
  };

  // Initialize on mount
  useEffect(() => {
    fetchStatus();
    fetchBalance();

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  return {
    status,
    balance,
    loading,
    error,
    refetch,
    updateAutoRefill,
    getCreditLedger,
    createCheckoutSession,
  };
};